import React from "react";
import PropTypes from "prop-types";
import { lighten, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Menu from "@material-ui/core/Menu";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { useHistory } from "react-router-dom";

import ReportsSummaryComponent from "./ReportsSummaryComponent";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        boxSizing: "border-box",
    },
    containerSectionResearch: {
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1rem",
        background: theme.palette.background.paper,
        borderRadius: 12,
        marginBottom: "2rem",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        boxSizing: "border-box",
    },
    containerManualResearch: {
        display: "flex",
        flexDirection: "column",
        width: "25rem",
    },
    sectionTitle: {
        fontSize: "1rem",
        fontWeight: 525,
        color: theme.palette.text.primary,
        marginBottom: "0.2rem",
    },
    searchField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: theme.palette.background.paper,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            "& fieldset": {
                borderColor: theme.palette.divider,
                borderWidth: "1px",
            },
            "&:hover fieldset": {
                borderColor: "#3b82f6",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#3b82f6",
                borderWidth: "2px",
            },
        },
        "& .MuiInputBase-input": {
            padding: "0.8rem 0.5rem",
            fontSize: "0.875rem",
        },
    },
    containerSearchBySatus: {
        display: "flex",
        flexDirection: "column",
    },
    containerStatusSelection: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "2rem",
    },
    statusSelect: {
        minWidth: "180px",
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: theme.palette.background.paper,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            "& fieldset": {
                borderColor: theme.palette.divider,
                borderWidth: "1px",
            },
            "&:hover fieldset": {
                borderColor: "#3b82f6",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#3b82f6",
                borderWidth: "2px",
            },
        },
        "& .MuiSelect-select": {
            padding: "12px 16px",
            fontSize: "0.875rem",
        },
    },
    searchButton: {
        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.875rem",
        padding: "12px 24px",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
        textTransform: "none",
        height: "40px",
        minHeight: "40px",
        "&:hover": {
            background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
            boxShadow: "0 6px 16px rgba(37, 99, 235, 0.4)",
        },
    },
    paper: {
        width: "100%",
        marginBottom: theme.spacing(2),
        borderRadius: 12,
        overflow: "hidden",
    },
    tableContainer: {
        borderRadius: 12,
        maxHeight: "none",
        height: "auto",
    },
    tableHead: {
        height: "4rem",
        background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
        "& th": {
            color: "#ffffff",
            fontWeight: 525,
            letterSpacing: "0.02em",
        },
        "& .MuiTableSortLabel-root": {
            color: "#ffffff",
            "&:hover": {
                color: "#ffffff",
            },
            "&.MuiTableSortLabel-active": {
                color: "#ffffff",
            },
        },
        "& .MuiTableSortLabel-icon": {
            color: "#ffffff !important",
        },
    },
    table: {
        minWidth: 750,
    },
    expandIcon: {
        cursor: "pointer",
        transition: "transform 0.2s ease-in-out",
    },
    expandedRow: {
        backgroundColor: theme.palette.action.hover,
    },
    expandedContent: {
        padding: "1.5rem",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        margin: "0.5rem 0",
    },
    playerCard: {
        display: "flex",
        alignItems: "center",
        padding: "1rem",
        backgroundColor: theme.palette.action.hover,
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        marginBottom: "1rem",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    },
    playerAvatar: {
        width: "48px",
        height: "48px",
        borderRadius: "50%",
        marginRight: "1rem",
        border: `2px solid ${theme.palette.divider}`,
        objectFit: "cover",
    },
    playerInfo: {
        flex: 1,
    },
    playerName: {
        fontSize: "1rem",
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: "0.25rem",
    },
    playerSteamId: {
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
        fontFamily: "monospace",
        marginBottom: "0.25rem",
    },
    playerCountry: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    eloBadge: {
        display: "inline-flex",
        alignItems: "center",
        padding: "0.25rem 0.75rem",
        backgroundColor: "#4d4d80",
        color: "#ffffff",
        borderRadius: "12px",
        fontSize: "0.75rem",
        fontWeight: 600,
        marginLeft: "auto",
    },
    reportDetails: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1rem",
        marginTop: "1rem",
    },
    detailSection: {
        backgroundColor: theme.palette.action.hover,
        padding: "1rem",
        borderRadius: "6px",
        border: `1px solid ${theme.palette.divider}`,
    },
    offenseChip: {
        display: "inline-block",
        backgroundColor: "#fed7d7",
        color: "#c53030",
        padding: "0.25rem 0.5rem",
        borderRadius: "4px",
        fontSize: "0.75rem",
        fontWeight: 500,
        margin: "0.125rem",
    },
    remarksText: {
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
        fontStyle: "italic",
    },
    matchInfo: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.75rem",
        backgroundColor: theme.palette.action.hover,
        borderRadius: "6px",
        marginTop: "1rem",
    },
    matchIdText: {
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
        fontFamily: "monospace",
        fontWeight: 500,
    },
    copyButton: {
        padding: "0.25rem",
        minWidth: "auto",
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    roleLabel: {
        display: "inline-flex",
        alignItems: "center",
        padding: "0.25rem 0.5rem",
        borderRadius: "4px",
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginLeft: "0.5rem",
    },
    reporterLabel: {
        backgroundColor: "#dcfce7",
        color: "#166534",
        border: "1px solid #bbf7d0",
    },
    reportedLabel: {
        backgroundColor: "#fef2f2",
        color: "#dc2626",
        border: "1px solid #fecaca",
    },
    visuallyHidden: {
        border: 0,
        clip: "rect(0 0 0 0)",
        height: 1,
        margin: -1,
        overflow: "hidden",
        padding: 0,
        position: "absolute",
        top: 20,
        width: 1,
    },
    pending: {
        background: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.75rem",
        letterSpacing: "0.05em",
        borderRadius: 8,
        padding: "6px 12px",
        boxShadow: "0 2px 4px rgba(100, 116, 139, 0.25)",
        textTransform: "uppercase",
    },
    resolved: {
        background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.75rem",
        letterSpacing: "0.05em",
        borderRadius: 8,
        padding: "6px 12px",
        boxShadow: "0 2px 4px rgba(34, 197, 94, 0.25)",
        textTransform: "uppercase",
    },
    dismissed: {
        background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.75rem",
        letterSpacing: "0.05em",
        borderRadius: 8,
        padding: "6px 12px",
        boxShadow: "0 2px 4px rgba(220, 38, 38, 0.25)",
        textTransform: "uppercase",
    },
    matchIdContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem",
        maxWidth: "120px",
        margin: "0 auto",
    },
    matchIdText: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: "center",
        whiteSpace: "nowrap",
        flex: 1,
        fontSize: "0.875rem",
        color: "#666",
    },
    copyButton: {
        padding: "4px",
        minWidth: "auto",
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
    },
}));

const headCells = [
    {
        id: "createdAt",
        numeric: false,
        width: 140,
        disablePadding: false,
        align: "center",
        label: "Report Date",
    },
    {
        id: "reportedSteamId",
        numeric: false,
        width: 180,
        disablePadding: true,
        align: "center",
        label: "Reported Steam ID",
    },
    {
        id: "status",
        width: 100,
        numeric: false,
        disablePadding: true,
        align: "center",
        label: "Status",
    },
    {
        id: "offenses",
        align: "center",
        width: 120,
        numeric: false,
        disablePadding: false,
        label: "Offenses",
    },
    {
        id: "remarks",
        align: "center",
        width: 200,
        numeric: false,
        disablePadding: false,
        label: "Remarks",
    },
    {
        id: "matchId",
        align: "center",
        width: 140,
        numeric: false,
        disablePadding: true,
        label: "Match ID",
    },
    {
        id: "actions",
        numeric: false,
        align: "center",
        width: 80,
        disablePadding: true,
        label: "Actions",
    },
];

function EnhancedTableHead(props) {
    const {
        classes,
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        onRequestSort,
    } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead className={classes.tableHead}>
            <TableRow>
                <TableCell padding="checkbox">

                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        style={"width" in headCell ? { width: headCell.width } : {}}
                        align={headCell.align || "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            style={{
                                ...("align" in headCell ? { textAlign: headCell.align } : {}),
                                gap: 8,
                            }}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            <span
                                style={
                                    "align" in headCell && headCell.align === "center"
                                        ? { position: "relative", left: 10 }
                                        : {}
                                }
                            >
                                {headCell.label}
                            </span>
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function ReportedPlayerTable({ rows = [] }) {
    const classes = useStyles();
    const history = useHistory();
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("createdAt");
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [searchByStatus, setSearchByStatus] = React.useState("");
    const [searchText, setSearchText] = React.useState("");
    const [filteredRows, setFilteredRows] = React.useState(rows || []);
    const [currentFilter, setCurrentFilter] = React.useState("");
    const [expandedRows, setExpandedRows] = React.useState(new Set());
    const [copiedSteamId, setCopiedSteamId] = React.useState(false);
    const [copiedMatchId, setCopiedMatchId] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedReportId, setSelectedReportId] = React.useState(null);

    console.log(rows);

    React.useEffect(() => {
        setFilteredRows(rows || []);
    }, [rows]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const highlightText = (text, searchTerm) => {
        // Handle undefined, null, or empty text
        if (!text) return text;
        if (!searchTerm.trim()) return text;

        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '2px 4px', borderRadius: '3px' }}>
                    {part}
                </mark>
            ) : part
        );
    };

    const handleSearchTextChange = (event) => {
        const searchValue = event.target.value;
        setSearchText(searchValue);

        const safeRows = rows || [];
        if (searchValue.trim() === "") {
            setFilteredRows(safeRows);
        } else {
            const filtered = safeRows.filter(row => {
                const searchLower = searchValue.toLowerCase();

                // Search in Steam ID
                const steamIdMatch = row.reportedSteamId && row.reportedSteamId.toLowerCase().includes(searchLower);

                // Search in offenses
                let offenseMatch = false;
                if (row.offenses) {
                    const offenseKeys = Object.keys(row.offenses).filter(key => row.offenses[key]);
                    offenseMatch = offenseKeys.some(offense =>
                        offense.toLowerCase().includes(searchLower)
                    );
                }

                return steamIdMatch || offenseMatch;
            });
            setFilteredRows(filtered);
        }
        setPage(0);
    };

    const handleSearchByStatus = () => {
        setCurrentFilter(searchByStatus);

        const safeRows = rows || [];
        if (searchByStatus === "" || searchByStatus === "all") {
            setFilteredRows(safeRows);
        } else {
            const filtered = safeRows.filter(row =>
                row.status && row.status.toLowerCase() === searchByStatus.toLowerCase()
            );
            setFilteredRows(filtered);
        }
        setPage(0);
    };

    const toggleRowExpansion = (rowId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(rowId)) {
            newExpandedRows.delete(rowId);
        } else {
            newExpandedRows.add(rowId);
        }
        setExpandedRows(newExpandedRows);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + " - " + date.toLocaleTimeString();
    };

    const formatOffenses = (offenses) => {
        if (!offenses) return "None";
        const offenseList = Object.keys(offenses).filter(key => offenses[key]);
        return offenseList.length > 0 ? offenseList.join(", ") : "None";
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case "pending":
                return classes.pending;
            case "resolved":
                return classes.resolved;
            case "dismissed":
                return classes.dismissed;
            default:
                return classes.pending;
        }
    };

    const handleCopyMatchId = (matchId) => {
        navigator.clipboard.writeText(matchId).then(() => {
            setCopiedMatchId(true);
            setTimeout(() => setCopiedMatchId(false), 1500);
        }).catch(err => {
            console.error('Failed to copy match ID:', err);
        });
    };

    const handleCopySteamId = (steamId) => {
        navigator.clipboard.writeText(steamId).then(() => {
            setCopiedSteamId(true);
            setTimeout(() => setCopiedSteamId(false), 1500);
        }).catch(err => {
            console.error('Failed to copy Steam ID:', err);
        });
    };

    const handleOpenMenu = (event, reportId) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedReportId(reportId);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedReportId(null);
    };

    const handleManageReport = () => {
        if (selectedReportId) {
            history.push(`/admin/reports/${selectedReportId}`);
        }
        handleCloseMenu();
    };

    const handleQuickStatusChange = async (status) => {
        if (!selectedReportId) return;
        
        // TODO: Implement API call to update status
        console.log(`Quick status change to ${status} for report ${selectedReportId}`);
        handleCloseMenu();
    };

    const safeRows = rows || [];
    const displayRows = (searchText.trim() !== "" || currentFilter !== "") ? filteredRows : safeRows;
    const emptyRows =
        rowsPerPage - Math.min(rowsPerPage, displayRows.length - page * rowsPerPage);

    return (
        <div className={classes.root}>
            <ReportsSummaryComponent displayRows={displayRows} />
            <div className={classes.containerSectionResearch}>
                <div className={classes.containerManualResearch}>
                    <Typography className={classes.sectionTitle}>What are you looking for?</Typography>
                    <TextField
                        className={classes.searchField}
                        placeholder="Search by Steam ID or offenses..."
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={searchText}
                        onChange={handleSearchTextChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: '#666' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
                <div className={classes.containerSearchBySatus}>
                    <Typography className={classes.sectionTitle}>Status</Typography>
                    <div className={classes.containerStatusSelection}>
                        <FormControl variant="outlined" size="small" className={classes.statusSelect}>
                            <Select
                                displayEmpty
                                value={searchByStatus}
                                onChange={(e) => setSearchByStatus(e.target.value)}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <Typography style={{ color: '#999' }}>Select status</Typography>;
                                    }
                                    return selected === 'all' ? 'All' : selected.charAt(0).toUpperCase() + selected.slice(1);
                                }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="resolved">Resolved</MenuItem>
                                <MenuItem value="dismissed">Dismissed</MenuItem>
                            </Select>
                        </FormControl>
                        <Button className={classes.searchButton} onClick={handleSearchByStatus}>
                            Search
                        </Button>
                    </div>
                </div>
            </div>
            <Paper className={classes.paper}>
                <TableContainer className={classes.tableContainer}>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size="medium"
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            classes={classes}
                            numSelected={0}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={() => { }}
                            onRequestSort={handleRequestSort}
                            rowCount={displayRows.length}
                        />
                        <TableBody>
                            {displayRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" style={{ padding: "2rem" }}>
                                        <Typography variant="h6" style={{ color: "#666", fontWeight: 500 }}>
                                            No reports found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stableSort(displayRows, getComparator(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        const isExpanded = expandedRows.has(row._id);

                                        return (
                                            <React.Fragment key={row._id}>
                                                <TableRow
                                                    hover
                                                    className={isExpanded ? classes.expandedRow : ""}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <IconButton
                                                            onClick={() => toggleRowExpansion(row._id)}
                                                            className={classes.expandIcon}
                                                        >
                                                            {isExpanded ? (
                                                                <KeyboardArrowDownIcon />
                                                            ) : (
                                                                <KeyboardArrowRightIcon />
                                                            )}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" align="center">
                                                        {formatDate(row.createdAt)}
                                                    </TableCell>
                                                    <TableCell padding="none" align="center" style={{ textAlign: "center" }}>
                                                        <div className={classes.matchIdContainer}>
                                                            <span className={classes.matchIdText} title={row.reportedSteamId}>
                                                                {highlightText(row.reportedSteamId, searchText)}
                                                            </span>
                                                            <Tooltip title={copiedSteamId ? "Copied!" : "Copy Steam ID"}>
                                                                <IconButton
                                                                    className={classes.copyButton}
                                                                    onClick={() => handleCopySteamId(row.reportedSteamId)}
                                                                    size="small"
                                                                >
                                                                    <FileCopyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell padding="none" align="center">
                                                        <Chip
                                                            className={getStatusClass(row.status)}
                                                            label={row.status?.toUpperCase() || "PENDING"}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center" style={{ padding: "0.75rem 1rem" }}>
                                                        {row.offenses ? highlightText(formatOffenses(row.offenses), searchText) : "None"}
                                                    </TableCell>
                                                    <TableCell align="center" style={{ padding: "0.75rem 1.25rem" }}>
                                                        {row.remarks || "No remarks"}
                                                    </TableCell>
                                                    <TableCell padding="none" align="center" style={{ textAlign: "center" }}>
                                                        <div className={classes.matchIdContainer}>
                                                            <span className={classes.matchIdText} title={row.matchId}>
                                                                {row.matchId}
                                                            </span>
                                                            <Tooltip title={copiedMatchId ? "Copied!" : "Copy Match ID"}>
                                                                <IconButton
                                                                    className={classes.copyButton}
                                                                    onClick={() => handleCopyMatchId(row.matchId)}
                                                                    size="small"
                                                                >
                                                                    <FileCopyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            aria-haspopup="true"
                                                            onClick={(e) => handleOpenMenu(e, row._id)}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                                {isExpanded && (
                                                    <TableRow>
                                                        <TableCell colSpan={8} style={{ padding: 0 }}>
                                                            <div className={classes.expandedContent}>
                                                                {/* Reporter Player Card */}
                                                                <div className={classes.playerCard}>
                                                                    <img
                                                                        src={row.reporterUserData?.picture}
                                                                        alt="Reporter Avatar"
                                                                        className={classes.playerAvatar}
                                                                    />
                                                                    <div className={classes.playerInfo}>
                                                                        <div className={classes.playerName}>
                                                                            {row.reporterUserData?.nickname || 'Unknown'}
                                                                            <span className={`${classes.roleLabel} ${classes.reporterLabel}`}>
                                                                                REPORTER
                                                                            </span>
                                                                        </div>
                                                                        <div className={classes.playerSteamId}>
                                                                            {row.reporterSteamId}
                                                                        </div>
                                                                        <div className={classes.playerCountry}>
                                                                            {row.reporterUserData?.country || 'Unknown'}
                                                                        </div>
                                                                    </div>
                                                                    <div className={classes.eloBadge}>
                                                                        ELO: {row.reporterUserData?.elo || 'N/A'}
                                                                    </div>
                                                                </div>

                                                                {/* Reported Player Card */}
                                                                <div className={classes.playerCard}>
                                                                    <img
                                                                        src={row.reportedUserData?.picture}
                                                                        alt="Reported Player Avatar"
                                                                        className={classes.playerAvatar}
                                                                    />
                                                                    <div className={classes.playerInfo}>
                                                                        <div className={classes.playerName}>
                                                                            {row.reportedUserData?.nickname || 'Unknown'}
                                                                            <span className={`${classes.roleLabel} ${classes.reportedLabel}`}>
                                                                                REPORTED
                                                                            </span>
                                                                        </div>
                                                                        <div className={classes.playerSteamId}>
                                                                            {row.reportedSteamId}
                                                                        </div>
                                                                        <div className={classes.playerCountry}>
                                                                            {row.reportedUserData?.country || 'Unknown'}
                                                                        </div>
                                                                    </div>
                                                                    <div className={classes.eloBadge}>
                                                                        ELO: {row.reportedUserData?.elo || 'N/A'}
                                                                    </div>
                                                                </div>

                                                                {/* Report Details Grid */}
                                                                <div className={classes.reportDetails}>
                                                                    {/* Offenses Section */}
                                                                    <div className={classes.detailSection}>
                                                                        <div className={classes.sectionTitle}>Reported Offenses</div>
                                                                        <div>
                                                                            {row.offenses && Object.keys(row.offenses).filter(key => row.offenses[key]).length > 0 ? (
                                                                                Object.keys(row.offenses)
                                                                                    .filter(key => row.offenses[key])
                                                                                    .map((offense, index) => (
                                                                                        <span key={index} className={classes.offenseChip}>
                                                                                            {offense.replace(/_/g, ' ').toUpperCase()}
                                                                                        </span>
                                                                                    ))
                                                                            ) : (
                                                                                <span style={{ color: '#718096', fontStyle: 'italic' }}>No specific offenses reported</span>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Remarks Section */}
                                                                    <div className={classes.detailSection}>
                                                                        <div className={classes.sectionTitle}>Reporter Remarks</div>
                                                                        <div className={classes.remarksText}>
                                                                            {row.remarks || 'No additional remarks provided'}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Match Information */}
                                                                <div className={classes.matchInfo}>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.25rem' }}>
                                                                            MATCH ID
                                                                        </div>
                                                                        <div className={classes.matchIdText}>
                                                                            {row.matchId}
                                                                        </div>
                                                                    </div>
                                                                    <Tooltip title={copiedMatchId ? "Copied!" : "Copy Match ID"}>
                                                                        <IconButton
                                                                            className={classes.copyButton}
                                                                            onClick={() => handleCopyMatchId(row.matchId)}
                                                                            size="small"
                                                                        >
                                                                            <FileCopyIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                            )}
                            {emptyRows > 0 && displayRows.length > 0 && displayRows.length >= rowsPerPage && (
                                <TableRow style={{ height: 40 * emptyRows }}>
                                    <TableCell colSpan={8} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={displayRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleManageReport}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Manage Report</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleQuickStatusChange('in_review')}>
                    <ListItemIcon>
                        <VisibilityIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Mark as In Review</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleQuickStatusChange('resolved')}>
                    <ListItemIcon>
                        <CheckCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Mark as Resolved</ListItemText>
                </MenuItem>
            </Menu>
        </div>
    )
}

ReportedPlayerTable.propTypes = {
    rows: PropTypes.array,
};